import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Button } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTheme } from "@mui/material/styles";

const BUTTON_WIDTH = 70;

const NavigationTabs = ({
  items = [],
  selectedItem,
  onItemSelect,
  isFacturacion = false,
  isFull = false,
  innerWidth = false,
  buttonStyle = "default", // "default", "navigation" o "custom"
  itemsPerPage = 4,
  dynamicColor, // Nuevo prop para el color dinámico
}) => {
  const theme = useTheme();
  const colorTexto = theme.bandera?.colorTextoBoton || "#000";
  const [start, setStart] = useState(0);
  const containerRef = useRef(null);

  // Función para obtener el color a usar
  const getColorToUse = () => {
    return dynamicColor || theme.bandera.colorSecundario;
  };

  const handlePrev = () => {
    setStart(prev => {
      const isLastPage = prev >= items.length - itemsPerPage;
      if (isLastPage) {
        return prev - itemsPerPage;
      }
      return Math.max(prev - itemsPerPage, 0);
    });
  };

  const handleNext = () => {
    setStart(prev => {
      const newStart = prev + itemsPerPage;
      if (newStart + itemsPerPage > items.length) {
        return items.length - (items.length % itemsPerPage || itemsPerPage);
      }
      return newStart;
    });
  };

  const getGap = () => {
    return buttonStyle === "custom" ? 0 : 1;
  };

  const translateX = -(start * (BUTTON_WIDTH + getGap()));

  const handleClick = item => {
    if (onItemSelect) {
      onItemSelect(item);
    }
  };

  useEffect(() => {
    function updateVisibleCount() {
      setStart(prev => Math.min(prev, Math.max(0, items.length - itemsPerPage)));
    }
    updateVisibleCount();
    window.addEventListener("resize", updateVisibleCount);
    return () => window.removeEventListener("resize", updateVisibleCount);
  }, [items.length, itemsPerPage]);

  const renderNavigationButton = (item, idx) => {
    // Manejar botón FULL con imagen
    if (item.image) {
      return (
        <Box
          key={idx}
          sx={{
            minWidth: buttonStyle === "navigation" ? "auto" : BUTTON_WIDTH,
            flex: buttonStyle === "navigation" ? 1 : "0 0 auto",
            display: "flex",
            justifyContent: "center",
            mx: buttonStyle === "navigation" ? 0.5 : 0,
            mr: -1,
          }}
        >
          <Button
            fullWidth={buttonStyle === "navigation"}
            variant="contained"
              sx={{
                borderRadius: 1,
                fontWeight: "bold",
                whiteSpace: "nowrap",
                backgroundColor: "#fff",
                color: "#000",
                ":hover": {
                  backgroundColor: getColorToUse(),
                },
              p: 0,
              border: "1px solid #000",
              position: "relative", // Agregado para el posicionamiento absoluto de la imagen
              ...(buttonStyle === "navigation" && {
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }),
            }}
            onClick={item.onClick}
          >
            <img
              src={item.image}
              alt={item.label}
              style={{
                position: "absolute",
                width: theme.bandera.navigationLogoTiendaAncho,
                height: 'auto',
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            />
          </Button>
        </Box>
      );
    }

    // Botón normal con ícono
    return (
      <Box
        key={idx}
        sx={{
          minWidth: buttonStyle === "navigation" ? "auto" : BUTTON_WIDTH,
          flex: buttonStyle === "navigation" ? 1 : "0 0 auto",
          display: "flex",
          justifyContent: "center",
          mx: buttonStyle === "navigation" && !innerWidth ? 0.25 : buttonStyle === "navigation" ? 0.5 : 0,
          mr: isFull ? -1 : 0,
        }}
      >
        <Button
          fullWidth={buttonStyle === "navigation"}
          variant="contained"
            sx={{
              borderRadius: 1,
              fontWeight: "bold",
              fontSize: isFull ? 10 : 12,
              width: isFull ? "90px" : "110px",
              whiteSpace: "nowrap",
              backgroundColor: selectedItem === item.id ? getColorToUse() : getColorToUse(),
              color: colorTexto,
              ":hover": {
                backgroundColor: getColorToUse(),
              },
            ...(buttonStyle === "navigation" && {
              height: "36px",
            }),
          }}
          onClick={item.onClick}
        >
          {item.icon && <item.icon sx={{ mr: 0.5, fontSize: 17 }} />}
          {item.label}
        </Button>
      </Box>
    );
  };

  const renderCustomButton = (item, idx) => {
    const isActive = selectedItem === item.id;

    return (
      <Box
        key={idx}
        sx={{
          minWidth: BUTTON_WIDTH,
          flex: "0 0 auto",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {item.customComponent ? (
          <item.customComponent
            {...item.componentProps}
            active={isActive}
            dynamicColor={dynamicColor}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: isActive ? getColorToUse() : "#fff",
                color: isActive ? colorTexto : "#000",
                ...item.componentProps.sx,
              }}
            onClick={() => handleClick(item)}
          />
        ) : (
          renderNavigationButton(item, idx)
        )}
      </Box>
    );
  };

  return (
    <Box ref={containerRef} display="flex" alignItems="center" width="100%" sx={{ flexShrink: 0 }}>
      {innerWidth && items.length > itemsPerPage && (
        <IconButton onClick={handlePrev} disabled={start === 0} sx={{ mt: buttonStyle === "navigation" ? 0 : -6, ...(isFacturacion && { ml: -1, p: 0 }) }}>
          <ArrowBackIosIcon />
        </IconButton>
      )}

      <Box
        sx={{
          overflow: isFacturacion && !isFull ? "unset" : "hidden",
          flex: 1,
          ...(isFacturacion &&
            !isFull && {
              ml: -0.5,
            }),
          position: "relative",
          ...(!innerWidth && {
            display: "flex",
            justifyContent: "center",
          }),
          height: buttonStyle === "navigation" ? "auto" : 100,
        }}
      >
        <Box
          sx={{
            display: "flex",
            ...(innerWidth && {
              transform: `translateX(${translateX}px)`,
              transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
              willChange: "transform",
              "& > *:not(:last-child)": {
                marginRight: `${getGap()}px`,
              },
            }),
            ...(buttonStyle === "navigation" &&
              !innerWidth && {
                width: "100%",
              }),
            ...(buttonStyle === "custom" &&
              !innerWidth && {
                justifyContent: "center",
                "& > *:not(:last-child)": {
                  marginRight: `${getGap()}px`,
                },
              }),
          }}
        >
          {items.map((item, idx) => {
            if (buttonStyle === "navigation") {
              return renderNavigationButton(item, idx);
            } else if (buttonStyle === "custom") {
              return renderCustomButton(item, idx);
            } else {
              return renderCustomButton(item, idx);
            }
          })}
        </Box>
      </Box>

      {innerWidth && items.length > itemsPerPage && (
        <IconButton
          onClick={handleNext}
          disabled={start >= items.length - (items.length % itemsPerPage || itemsPerPage)}
          sx={{ mt: buttonStyle === "navigation" ? 0 : -6, ...(isFacturacion && { mr: -1, p: 0 }) }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default NavigationTabs;
